import { Config } from 'config/envConfig';
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { alert, AlertType, Input } from '@quyva/shared-ui';
import { Brand } from './brand';
import { PoweredByInfo } from './powereByComponent';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faExclamationTriangle,
    faKey,
    faLockOpen,
    faMailReply,
    faSpinner,
    faEye,
    faEyeSlash,
} from '@fortawesome/free-solid-svg-icons';
import { getUrl } from './pdfDisplay/utils';
import { isFeatureEnabled } from '@quyva/common';
import { Features, OtherFeatures } from '@quyva/common';
import { useFeatureFlags } from '../context';

const ChangePassword = () => {
    const { featureFlagsData } = useFeatureFlags();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [token, setToken] = useState('');
    const [validToken, setValidToken] = useState(true);
    const [loading, setLoading] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const token = searchParams.get('token');
        if (token) {
            setToken(token);
        } else {
            setValidToken(false);
            // console.error('Token not found in URL');
        }
    }, [location]);

    const handleChangePassword = async () => {
        try {
            setLoading(true);
            if (!password || !confirmPassword) {
                alert(AlertType.ERROR, 'Password fields cannot be empty!');
                return;
            }

            if (password !== confirmPassword) {
                alert(AlertType.ERROR, 'Passwords do not match!');
                return;
            }

            const dynamicUrl = getUrl(
                `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/reset`
            );
            const response = await fetch(dynamicUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ newPassword: password, token }),
            });

            if (response.ok) {
                alert(AlertType.SUCCESS, 'Password changed successfully!');
                navigate('/login');
            } else {
                const errorData = await response.json();
                console.error('Error changing password', errorData);
                alert(AlertType.ERROR, `Error: ${errorData.error}`);
            }
        } catch (error) {
            console.error('Error changing password', error);
            alert(AlertType.ERROR, 'An error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className='flex items-center justify-center min-h-screen bg-gradient-primary'>
            <div className='w-full max-w-[500px] flex-col border bg-backgrounds-white p-6 shadow-lg rounded-xl'>
                <div className='mb-8 w-full lg:w-3/5 mx-auto'>
                    {isFeatureEnabled(
                        [Features.OTHERS, OtherFeatures.LOGO],
                        featureFlagsData
                    ) && <Brand iconClassName={'w-20 h-20 mr-3'} />}
                </div>
                {validToken ? (
                    <>
                        <h2 className='text-xl font-semibold mb-6 text-center text-texts-secondary300'>
                            <FontAwesomeIcon
                                icon={faLockOpen}
                                className='mr-2'
                            />
                            Change Password
                        </h2>
                        <div className='flex flex-col text-sm w-full'>
                            <div className='relative w-full mb-5'>
                                <Input
                                    className='rounded-xl border p-3 w-full hover:outline-none focus:outline-backgrounds-primary200 hover:border-backgrounds-primary300 shadow-sm'
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder='New Password'
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                />
                                {password && (
                                    <FontAwesomeIcon
                                        icon={showPassword ? faEyeSlash : faEye}
                                        className='absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer font-semibold text-texts-secondary300'
                                        onClick={() => setShowPassword(!showPassword)}
                                    />
                                )}
                            </div>
                            <div className='relative w-full mb-5'>
                                <Input
                                    className='rounded-xl border p-3 w-full hover:outline-none focus:outline-backgrounds-primary200 hover:border-backgrounds-primary300 shadow-sm'
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    placeholder='Confirm New Password'
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                />
                                {confirmPassword && (
                                    <FontAwesomeIcon
                                        icon={showConfirmPassword ? faEyeSlash : faEye}
                                        className='absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer font-semibold text-texts-secondary300'
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    />
                                )}
                            </div>
                            <button
                                className='flex w-full font-semibold items-center justify-center text-texts-primary bg-buttons-primary border border-1 shadow-inner hover:bg-gradient-buttons hover:border-buttons-primary hover:text-texts-primary p-3 mt-2 rounded-lg'
                                type='submit'
                                onClick={handleChangePassword}
                            >
                                <FontAwesomeIcon
                                    icon={loading ? faSpinner : faKey}
                                    className={
                                        loading
                                            ? 'animate-spin mr-1.5'
                                            : 'mr-1.5'
                                    }
                                />
                                <span className=''>
                                    {loading
                                        ? 'Changing Password'
                                        : 'Change Password'}
                                </span>
                            </button>
                        </div>
                    </>
                ) : (
                    <div className='text-center text-buttons-alert400 font-semibold'>
                        <FontAwesomeIcon
                            icon={faExclamationTriangle}
                            className='mr-1.5'
                        />
                        The reset password link has expired or is invalid.
                    </div>
                )}
                <div className='mt-4 p-1 text-center text-sm font-semibold text-texts-tertiary hover:text-texts-tertiary400'>
                    <Link to='/login'>
                        <FontAwesomeIcon
                            icon={faMailReply}
                            className='mr-1.5'
                        />
                        Back to Login
                    </Link>
                </div>
                <div className='w-full flex justify-center items-center mt-8'>
                    <PoweredByInfo />
                </div>
            </div>
        </div>
    );
};

export default ChangePassword;
